/**
 * Supabase/Postgres returns `quotes` rows with snake_case keys.
 * Dashboard UI expects camelCase (clientId, editedOn, invoiceData, …).
 *
 * After save we merge `{ ...existingQuote, ...newQuote }`. Existing state is camelCase;
 * `newQuote` uses snake_case. Prefer snake when the key exists on the row so updates win.
 */
function snakeFirst(row, snakeKey, camelKey, fallback) {
  if (Object.prototype.hasOwnProperty.call(row, snakeKey)) {
    const v = row[snakeKey];
    return v === undefined || v === null ? fallback : v;
  }
  const c = row[camelKey];
  if (c !== undefined && c !== null) return c;
  const s = row[snakeKey];
  if (s !== undefined && s !== null) return s;
  return fallback;
}

export function normalizeQuoteFromDb(row) {
  if (!row || typeof row !== 'object') return row;

  const fromSnake = row.custom_quote_data;
  const fromCamel = row.customQuoteData;
  // Camel first, then snake — so DB / save payload keys win over stale in-memory camelCase.
  const customQuoteData = {
    ...(typeof fromCamel === 'object' && fromCamel ? fromCamel : {}),
    ...(typeof fromSnake === 'object' && fromSnake ? fromSnake : {}),
  };
  const taxFromCqd = customQuoteData.taxEnabled;
  const finalTaxFromCqd = customQuoteData.finalTaxEnabled;

  const clientIdRaw = snakeFirst(row, 'client_id', 'clientId', '');
  const clientId = clientIdRaw === null || clientIdRaw === undefined ? '' : clientIdRaw;

  return {
    ...row,
    clientId,
    clientEmail: snakeFirst(row, 'client_email', 'clientEmail', undefined),
    quoteNumber: snakeFirst(row, 'quote_number', 'quoteNumber', '') || '',
    editedOn: snakeFirst(row, 'edited_on', 'editedOn', null),
    createdAt: row.createdAt ?? row.created_at,
    updatedAt: row.updatedAt ?? row.updated_at,
    markupType: snakeFirst(row, 'markup_type', 'markupType', 'percentage') || 'percentage',
    overheadType: snakeFirst(row, 'overhead_type', 'overheadType', customQuoteData.overheadType || 'amount') || 'amount',
    taxRate:
      snakeFirst(row, 'tax_rate', 'taxRate', undefined) ??
      (customQuoteData.taxRate != null && customQuoteData.taxRate !== ''
        ? customQuoteData.taxRate
        : ''),
    taxEnabled:
      taxFromCqd !== undefined
        ? taxFromCqd
        : row.taxEnabled !== undefined
          ? row.taxEnabled
          : true,
    finalTaxEnabled:
      finalTaxFromCqd === true ||
      row.finalTaxEnabled === true,
    customQuoteData,
    description:
      row.description ??
      customQuoteData.description ??
      '',
    invoiceData: snakeFirst(row, 'invoice_data', 'invoiceData', undefined),
    pdfData: snakeFirst(row, 'pdf_data', 'pdfData', undefined),
    agreedPrice: snakeFirst(row, 'agreed_price', 'agreedPrice', undefined),
    agreedPriceSetAt: snakeFirst(row, 'agreed_price_set_at', 'agreedPriceSetAt', undefined),
    quoteSent: snakeFirst(row, 'quote_sent', 'quoteSent', undefined),
    invoiceSent: snakeFirst(row, 'invoice_sent', 'invoiceSent', undefined),
    quoteSentAt: snakeFirst(row, 'quote_sent_at', 'quoteSentAt', null),
    invoiceSentAt: snakeFirst(row, 'invoice_sent_at', 'invoiceSentAt', null),
    invoicePaymentStatus: snakeFirst(row, 'invoice_payment_status', 'invoicePaymentStatus', undefined),
    invoiceAmount: snakeFirst(row, 'invoice_amount', 'invoiceAmount', undefined),
    invoicePdfUrl: snakeFirst(row, 'invoice_pdf_url', 'invoicePdfUrl', undefined),
    pdfGeneratedAt: snakeFirst(row, 'pdf_generated_at', 'pdfGeneratedAt', undefined),
    pdfReference: snakeFirst(row, 'pdf_reference', 'pdfReference', undefined),
    invoicePdfGeneratedAt: snakeFirst(
      row,
      'invoice_pdf_generated_at',
      'invoicePdfGeneratedAt',
      undefined
    ),
    invoiceGeneratedAt: snakeFirst(row, 'invoice_generated_at', 'invoiceGeneratedAt', undefined),
    pdfUrl: snakeFirst(row, 'pdf_url', 'pdfUrl', undefined),
    userId: snakeFirst(row, 'user_id', 'userId', undefined),
  };
}
